import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useKitchenSettings, useUpdateKitchenSettings } from '@/features/pos/hooks/useKitchenSettings';
import { ArrowLeft, Save, Loader2, Clock, AlertTriangle } from 'lucide-react';
import { toast } from '@/components/ui/toast/useToast';

export const Route = createFileRoute('/apps/pos/admin/kitchen-settings/')({
  component: KitchenSettingsPage,
});

function KitchenSettingsPage() {
  const { data: settings, isLoading } = useKitchenSettings();
  const updateSettings = useUpdateKitchenSettings();

  const [formData, setFormData] = useState({
    default_prep_time: 15,
    food_prep_time: 20,
    beverage_prep_time: 5,
    dessert_prep_time: 10,
    warning_time: 10,
    urgent_time: 15,
    critical_time: 20,
    sound_enabled: true,
    auto_print_kitchen_ticket: false,
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        default_prep_time: settings.default_prep_time,
        food_prep_time: settings.food_prep_time,
        beverage_prep_time: settings.beverage_prep_time,
        dessert_prep_time: settings.dessert_prep_time,
        warning_time: settings.warning_time,
        urgent_time: settings.urgent_time,
        critical_time: settings.critical_time,
        sound_enabled: settings.sound_enabled,
        auto_print_kitchen_ticket: settings.auto_print_kitchen_ticket,
      });
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateSettings.mutateAsync(formData);
      toast.success('Sauvegardé', 'Paramètres cuisine mis à jour');
    } catch (error: any) {
      toast.error('Erreur', error.message);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Paramètres Cuisine
              </h1>
              <p className="text-sm text-gray-600">
                Configurer les temps de préparation et alertes
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Preparation Times */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-6 h-6 text-emerald-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Temps de Préparation
                </h2>
                <p className="text-sm text-gray-600">
                  Durée estimée pour préparer chaque type de produit (en minutes)
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="default_prep_time">Temps par défaut</Label>
                <Input
                  id="default_prep_time"
                  type="number"
                  min="1"
                  max="120"
                  value={formData.default_prep_time}
                  onChange={(e) => handleChange('default_prep_time', parseInt(e.target.value))}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Pour produits sans catégorie spécifique
                </p>
              </div>

              <div>
                <Label htmlFor="food_prep_time">Plats / Nourriture</Label>
                <Input
                  id="food_prep_time"
                  type="number"
                  min="1"
                  max="120"
                  value={formData.food_prep_time}
                  onChange={(e) => handleChange('food_prep_time', parseInt(e.target.value))}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Pizza, burgers, plats chauds, etc.
                </p>
              </div>

              <div>
                <Label htmlFor="beverage_prep_time">Boissons</Label>
                <Input
                  id="beverage_prep_time"
                  type="number"
                  min="1"
                  max="120"
                  value={formData.beverage_prep_time}
                  onChange={(e) => handleChange('beverage_prep_time', parseInt(e.target.value))}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Jus, sodas, café, etc.
                </p>
              </div>

              <div>
                <Label htmlFor="dessert_prep_time">Desserts</Label>
                <Input
                  id="dessert_prep_time"
                  type="number"
                  min="1"
                  max="120"
                  value={formData.dessert_prep_time}
                  onChange={(e) => handleChange('dessert_prep_time', parseInt(e.target.value))}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Gâteaux, glaces, pâtisseries, etc.
                </p>
              </div>
            </div>
          </Card>

          {/* Alert Thresholds */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Seuils d'Alerte
                </h2>
                <p className="text-sm text-gray-600">
                  Changement de couleur du minuteur selon le temps écoulé
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="warning_time">
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                      Avertissement (Jaune)
                    </span>
                  </Label>
                  <Input
                    id="warning_time"
                    type="number"
                    min="1"
                    max="120"
                    value={formData.warning_time}
                    onChange={(e) => handleChange('warning_time', parseInt(e.target.value))}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minuteur devient jaune après X minutes
                  </p>
                </div>

                <div>
                  <Label htmlFor="urgent_time">
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                      Urgent (Orange)
                    </span>
                  </Label>
                  <Input
                    id="urgent_time"
                    type="number"
                    min="1"
                    max="120"
                    value={formData.urgent_time}
                    onChange={(e) => handleChange('urgent_time', parseInt(e.target.value))}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minuteur devient orange après X minutes
                  </p>
                </div>

                <div>
                  <Label htmlFor="critical_time">
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                      Critique (Rouge)
                    </span>
                  </Label>
                  <Input
                    id="critical_time"
                    type="number"
                    min="1"
                    max="120"
                    value={formData.critical_time}
                    onChange={(e) => handleChange('critical_time', parseInt(e.target.value))}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minuteur devient rouge après X minutes
                  </p>
                </div>
              </div>

              {/* Color Preview */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-3">Aperçu des couleurs:</p>
                <div className="flex gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">5 min</div>
                    <p className="text-xs text-gray-600">0-{formData.warning_time} min</p>
                    <p className="text-xs text-green-600 font-medium">Normal</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">{formData.warning_time} min</div>
                    <p className="text-xs text-gray-600">{formData.warning_time}-{formData.urgent_time} min</p>
                    <p className="text-xs text-yellow-600 font-medium">Attention</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{formData.urgent_time} min</div>
                    <p className="text-xs text-gray-600">{formData.urgent_time}-{formData.critical_time} min</p>
                    <p className="text-xs text-orange-600 font-medium">Urgent</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{formData.critical_time}+ min</div>
                    <p className="text-xs text-gray-600">{formData.critical_time}+ min</p>
                    <p className="text-xs text-red-600 font-medium">Critique</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Notifications */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              Notifications
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label htmlFor="sound_enabled" className="text-base">
                    Son pour nouvelles commandes
                  </Label>
                  <p className="text-sm text-gray-600">
                    Jouer un son lorsqu'une nouvelle commande arrive
                  </p>
                </div>
                <Switch
                  id="sound_enabled"
                  checked={formData.sound_enabled}
                  onCheckedChange={(checked) => handleChange('sound_enabled', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label htmlFor="auto_print_kitchen_ticket" className="text-base">
                    Impression automatique ticket cuisine
                  </Label>
                  <p className="text-sm text-gray-600">
                    Imprimer automatiquement le ticket cuisine pour chaque commande
                  </p>
                </div>
                <Switch
                  id="auto_print_kitchen_ticket"
                  checked={formData.auto_print_kitchen_ticket}
                  onCheckedChange={(checked) => handleChange('auto_print_kitchen_ticket', checked)}
                />
              </div>
            </div>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => window.history.back()}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={updateSettings.isPending}
              className="gap-2"
            >
              {updateSettings.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Sauvegarder
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
